import React, { useState, useRef, Fragment, lazy, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { AnimatedSection } from '../shared/AnimatedSection';
import { Sign } from '../../data/signs';
interface VistaDelNucleoProps {
  sign: Sign;
}
// Editorial labels — order matters: Quote → Editorial → Ritual → Entity Spread
const SLIDE_LABELS = ['Página de Cita', 'Página Editorial', 'Página del Ritual', 'Entidad · Doble Página'] as const;
// Psychological microcopy per sign for the 4 slides
const SLIDE_MICROCOPY: Record<string, string[]> = {
  aries: ['El impulso que confundes con rabia', 'Por qué necesitas quemar para crear', 'El miedo a detenerte', 'La fuerza de tu fuego primordial'],
  tauro: ['El peso de tus propias raíces', 'Por qué te aferras a lo que ya terminó', 'El miedo silencioso al cambio', 'La paz que encuentras en la materia'],
  geminis: ['El ruido mental que no puedes apagar', 'Las versiones de ti que escondes', 'Por qué huyes a través de la palabra', 'La dualidad de tu propia mente'],
  cancer: ['La memoria emocional que no sueltas', 'Por qué proteges tanto tu vulnerabilidad', 'El miedo al abandono que te paraliza', 'La profundidad de tus mareas internas'],
  leo: ['El orgullo que esconde tu fragilidad', 'Por qué necesitas que te vean brillar', 'El miedo a no ser suficiente', 'La verdadera luz de tu presencia'],
  virgo: ['El control que usas para no sentir', 'Por qué tu mente nunca descansa', 'La autoexigencia que te consume', 'El orden que buscas en el caos'],
  libra: ['La paz que sostienes a costa tuya', 'Por qué te pierdes en los demás', 'El miedo al conflicto que te silencia', 'La armonía que realmente necesitas'],
  escorpio: ['La intensidad que intentas ocultar', 'Por qué el control es tu armadura', 'El miedo a ser verdaderamente vulnerable', 'El poder de tu propia oscuridad'],
  sagitario: ['La libertad que usas para escapar', 'Por qué siempre necesitas moverte', 'El miedo a sentirte atrapado', 'La verdad que buscas sin descanso'],
  capricornio: ['El peso que cargas en silencio', 'Por qué mides tu valor en logros', 'El miedo al fracaso que te aísla', 'La estructura de tu propia cima'],
  acuario: ['La distancia que usas para protegerte', 'Por qué te sientes siempre afuera', 'El miedo a perder tu independencia', 'La rebelión de tu propia mente'],
  piscis: ['Las emociones ajenas que haces tuyas', 'Por qué te disuelves en los demás', 'El escapismo que usas como refugio', 'La inmensidad de tu mar astral']
};
// Real preview images per sign. Order: Quote, Editorial, Ritual, Entity Spread.
// Empty arrays = elegant placeholder cards.
const VISTAS: Record<string, string[]> = {
  aries: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_27_p.m..png?v=1778725185', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_33_p.m..png?v=1778725186', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_39_p.m..png?v=1778725186', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_13_may_2026_10_13_45_p.m..png?v=1778725185'],
  tauro: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_0000000002dc722f8d3818395f038905.png?v=1778864384', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_000000004348722fa0cf70331196a642.png?v=1778864384', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000fda8722f9313b97597ee88d2.png?v=1778864384', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_000000000284722fb6792195e2b28d81.png?v=1778864384'],
  geminis: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000af3c722f8034ad1580457f9b.png?v=1778864344', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000707c722faea3984eab2e313f.png?v=1778864344', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_000000000a48722f9df1c33b63a0c0c1.png?v=1778864344', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000f204722f95ed4b2e927467e8.png?v=1778864344'],
  cancer: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_000000004a6c722fa931d2b602651c15.png?v=1778864137', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000709c722fb83a1bafba8d06c7.png?v=1778864137', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_000000000a4c722f957fa45b8c340f9b.png?v=1778864137', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000b944722fb109344428c406c4.png?v=1778864138'],
  leo: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/LEO_01.png?v=1779481051', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/LEO_04.png?v=1779481051', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/LEO_02.png?v=1779481051', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/LEO_03.png?v=1779481051'],
  libra: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_000000004d34722f9bc8aad6dbcbfac4.png?v=1778864419', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000aa4c71fd9530cd7b2c6d5462.png?v=1778864419', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_00000000d770722f8182a97ebcb84b52.png?v=1778864419', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/file_0000000014bc722fb100ec93cd9621d6.png?v=1778864419'],
  escorpio: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ESCORPIO_1.png?v=1779055925', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ESCORPIO_2.png?v=1779055946', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ESCORPIO_3.png?v=1779055959', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ESCORPIO_4.png?v=1779055971'],
  capricornio: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_15_may_2026_11_44_49_a.m..png?v=1778865221', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_15_may_2026_01_11_57_p.m..png?v=1778865221', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_15_may_2026_11_44_20_a.m..png?v=1778865221', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ChatGPT_Image_15_may_2026_11_45_59_a.m..png?v=1778865221'],
  piscis: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/PISCIS_1.png?v=1779115159', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/PISCIS_2.png?v=1779115159', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/PISCIS_3.png?v=1779115159', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/PISCIS_4.png?v=1779115159'],
  sagitario: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/SAGITARIO_01.png?v=1779488684', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/SAGITARIO_02.png?v=1779488684', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/SAGITARIO_03.png?v=1779488684', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/SAGITARIO_04.png?v=1779488684'],
  acuario: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ACUARIO_01.png?v=1779488684', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ACUARIO_02.png?v=1779488684', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ACUARIO_03.png?v=1779488684', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/ACUARIO_04.png?v=1779488684'],
  virgo: ['https://cdn.shopify.com/s/files/1/0661/3847/2542/files/VIRGO_01.png?v=1779488683', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/VIRGO_02.png?v=1779488683', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/VIRGO_03.png?v=1779488683', 'https://cdn.shopify.com/s/files/1/0661/3847/2542/files/VIRGO_04.png?v=1779488684']
};
// Build slides — at least 4 placeholders for signs without images yet.
function getSlides(sign: Sign): Array<{
  type: 'image' | 'placeholder';
  url?: string;
  label: string;
}> {
  const images = VISTAS[sign.id] || [];
  const microcopy = SLIDE_MICROCOPY[sign.id] || ['Fragmento 1', 'Fragmento 2', 'Fragmento 3', 'Fragmento 4'];
  if (images.length > 0) {
    return images.map((url, i) => ({
      type: 'image' as const,
      url,
      label: microcopy[i] || `Fragmento ${i + 1}`
    }));
  }
  return Array.from({
    length: 4
  }, (_, i) => ({
    type: 'placeholder' as const,
    label: microcopy[i] || `Fragmento ${i + 1}`
  }));
}
export function VistaDelNucleo({
  sign
}: VistaDelNucleoProps) {
  const slides = getSlides(sign);
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const goTo = (idx: number) => {
    const next = (idx + slides.length) % slides.length;
    setActive(next);
    const el = scrollRef.current;
    if (el) {
      const card = el.children[next] as HTMLElement | undefined;
      if (card) {
        el.scrollTo({
          left: card.offsetLeft - el.clientWidth / 2 + card.clientWidth / 2,
          behavior: 'smooth'
        });
      }
    }
  };
  return <section className="relative py-24 px-4 md:px-6 overflow-hidden bg-cosmic-black" data-id="element-1096">
      {/* Atmospheric backdrop */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-1097">
        <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse at 50% 30%, ${sign.colors.primary}18 0%, transparent 60%)`
      }} data-id="element-1098" />
        <div className="absolute inset-0" style={{
        background: `linear-gradient(180deg, #050508 0%, ${sign.colors.primary}08 50%, #050508 100%)`
      }} data-id="element-1099" />
      </div>

      {/* Floating embers */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1100">
        {[...Array(14)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
        backgroundColor: sign.colors.primary,
        left: `${i * 7.7 % 100}%`,
        top: `${i * 11 % 100}%`,
        boxShadow: `0 0 8px ${sign.colors.primary}`
      }} animate={{
        y: [-20, -60, -20],
        opacity: [0.15, 0.6, 0.15]
      }} transition={{
        duration: 7 + i % 5,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.4
      }} data-id="element-1101" />)}
      </div>

      <div className="relative container mx-auto max-w-7xl" data-id="element-1102">
        <AnimatedSection data-id="element-1103">
          <div className="text-center mb-4" data-id="element-1104">
            <motion.div className="inline-flex items-center gap-3 mb-5" initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} data-id="element-1105">
              <div className="h-px w-12" style={{
              background: `linear-gradient(90deg, transparent, ${sign.colors.primary})`
            }} data-id="element-1106" />
              <span className="tracking-[0.4em] uppercase font-serif text-[14px]" style={{
              color: sign.colors.accent
            }} data-id="element-1107">
                Vista del Núcleo
              </span>
              <div className="h-px w-12" style={{
              background: `linear-gradient(90deg, ${sign.colors.primary}, transparent)`
            }} data-id="element-1108" />
            </motion.div>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-center text-white mb-5" data-id="element-1109">
            Explora el Reino Interior
          </h2>
          <p className="text-center max-w-2xl mx-auto text-base md:text-lg font-serif italic mb-14" style={{
          color: `${sign.colors.accent}D0`,
          textShadow: `0 0 20px ${sign.colors.primary}30`
        }} data-id="element-1110">
            Cada Reino contiene símbolos, rituales y fragmentos visuales
            diseñados para representar una dimensión distinta de la identidad
            humana.
          </p>
        </AnimatedSection>

        {/* Carousel */}
        <div className="relative" data-id="element-1111">
          {/* Desktop arrows */}
          <button onClick={() => goTo(active - 1)} aria-label="Anterior" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 hover:scale-110" style={{
          backgroundColor: 'rgba(10,10,18,0.7)',
          border: `1px solid ${sign.colors.primary}60`,
          boxShadow: `0 0 20px ${sign.colors.primary}40`,
          color: sign.colors.accent,
          backdropFilter: 'blur(8px)'
        }} data-id="element-1112">
            <ChevronLeftIcon size={22} data-id="element-1113" />
          </button>
          <button onClick={() => goTo(active + 1)} aria-label="Siguiente" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 hover:scale-110" style={{
          backgroundColor: 'rgba(10,10,18,0.7)',
          border: `1px solid ${sign.colors.primary}60`,
          boxShadow: `0 0 20px ${sign.colors.primary}40`,
          color: sign.colors.accent,
          backdropFilter: 'blur(8px)'
        }} data-id="element-1114">
            <ChevronRightIcon size={22} data-id="element-1115" />
          </button>

          {/* Scrollable track */}
          <div ref={scrollRef} className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 px-[10vw] md:px-[18vw] scroll-smooth no-scrollbar" style={{
          scrollbarWidth: 'none'
        }} data-id="element-1116">
            {slides.map((slide, i) => {
            const isActive = i === active;
            return <motion.div key={i} className="snap-center flex-shrink-0 cursor-pointer" onClick={() => goTo(i)} animate={{
              scale: isActive ? 1 : 0.85,
              opacity: isActive ? 1 : 0.55
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 25
            }} whileHover={{
              scale: isActive ? 1.02 : 0.9
            }} data-id="element-1117">
                  <Frame slide={slide} sign={sign} isActive={isActive} index={i} data-id="element-1118" />
                </motion.div>;
          })}
          </div>

          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-1119">
            {slides.map((_, i) => <button key={i} onClick={() => goTo(i)} aria-label={`Ir al fragmento ${i + 1}`} className="transition-all duration-300" style={{
            width: i === active ? 28 : 8,
            height: 6,
            borderRadius: 3,
            backgroundColor: i === active ? sign.colors.primary : `${sign.colors.primary}40`,
            boxShadow: i === active ? `0 0 12px ${sign.colors.primary}` : 'none'
          }} data-id="element-1120" />)}
          </div>
        </div>
      </div>

      <style data-id="element-1121">{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
    </section>;
}
function Frame({
  slide,
  sign,
  isActive,
  index
}: {
  slide: {
    type: 'image' | 'placeholder';
    url?: string;
    label: string;
  };
  sign: Sign;
  isActive: boolean;
  index: number;
}) {
  return <div className="relative w-[78vw] sm:w-[60vw] md:w-[460px] lg:w-[520px] aspect-[3/4] rounded-2xl overflow-hidden group" style={{
    background: 'linear-gradient(160deg, rgba(10,10,18,0.95) 0%, rgba(5,5,10,1) 100%)',
    border: `1px solid ${sign.colors.primary}${isActive ? '90' : '40'}`,
    boxShadow: isActive ? `0 0 60px ${sign.colors.primary}50, 0 20px 60px rgba(0,0,0,0.6)` : `0 0 20px ${sign.colors.primary}20, 0 10px 30px rgba(0,0,0,0.4)`
  }} data-id="element-1122">
      {slide.type === 'image' && slide.url ? <>
          <motion.img src={slide.url} alt={`Fragmento ${index + 1} del Núcleo ${sign.name}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" initial={{
        scale: 1.1
      }} animate={{
        scale: isActive ? 1 : 1.05
      }} transition={{
        duration: 1.2
      }} loading="lazy" data-id="element-1123" />
          {/* Cinematic vignette */}
          <div className="absolute inset-0 pointer-events-none" style={{
        background: `radial-gradient(circle at 50% 50%, transparent 50%, rgba(0,0,0,0.5) 100%), linear-gradient(180deg, transparent 60%, rgba(5,5,8,0.85) 100%)`
      }} data-id="element-1124" />
          {/* Sign color wash */}
          <div className="absolute inset-0 pointer-events-none opacity-20" style={{
        background: `linear-gradient(180deg, transparent 0%, ${sign.colors.primary}40 100%)`
      }} data-id="element-1125" />
          {/* Hover glow */}
          <motion.div className="absolute inset-0 pointer-events-none rounded-2xl" animate={{
        opacity: isActive ? [0.4, 0.7, 0.4] : 0.3
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        boxShadow: `inset 0 0 80px ${sign.colors.primary}30`
      }} data-id="element-1126" />
          {/* Editorial label */}
          <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-3" data-id="element-1127">
            <span className="text-[10px] font-serif tracking-[0.35em] uppercase truncate" style={{
          color: sign.colors.accent,
          textShadow: '0 2px 12px rgba(0,0,0,0.9)'
        }} data-id="element-1128">
              {slide.label}
            </span>
            <span className="text-[10px] font-serif tracking-[0.25em] uppercase opacity-80 flex-shrink-0" style={{
          color: '#F5F0E1',
          textShadow: '0 2px 12px rgba(0,0,0,0.9)'
        }} data-id="element-1129">
              {sign.name} · {String(index + 1).padStart(2, '0')}
            </span>
          </div>
        </> : <PlaceholderFrame sign={sign} index={index} isActive={isActive} label={slide.label} data-id="element-1130" />}
    </div>;
}
function PlaceholderFrame({
  sign,
  index,
  isActive,
  label
}: {
  sign: Sign;
  index: number;
  isActive: boolean;
  label: string;
}) {
  return <div className="relative w-full h-full flex flex-col items-center justify-center overflow-hidden" data-id="element-1131">
      {/* Atmospheric radial glow */}
      <div className="absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 40%, ${sign.colors.primary}30 0%, transparent 65%)`
    }} data-id="element-1132" />

      {/* Floating particles */}
      {[...Array(10)].map((_, i) => <motion.div key={i} className="absolute w-[2px] h-[2px] rounded-full" style={{
      backgroundColor: sign.colors.primary,
      left: `${10 + i * 9}%`,
      top: `${20 + i % 4 * 20}%`,
      boxShadow: `0 0 8px ${sign.colors.primary}`
    }} animate={{
      y: [-10, -30, -10],
      opacity: [0.2, 0.7, 0.2]
    }} transition={{
      duration: 5 + i % 3,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.3
    }} data-id="element-1133" />)}

      {/* Ornamental frame inset */}
      <div className="absolute inset-6 rounded-xl pointer-events-none" style={{
      border: `1px solid ${sign.colors.primary}40`,
      boxShadow: `inset 0 0 30px ${sign.colors.primary}15`
    }} data-id="element-1134" />

      {/* Sacred sigil */}
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 70,
      repeat: Infinity,
      ease: 'linear'
    }} className="relative mb-8" data-id="element-1135">
        <motion.div className="absolute inset-0 rounded-full" animate={{
        scale: isActive ? [1, 1.2, 1] : 1,
        opacity: isActive ? [0.4, 0.8, 0.4] : 0.5
      }} transition={{
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} style={{
        boxShadow: `0 0 40px ${sign.colors.primary}80`
      }} data-id="element-1136" />
        <svg width="88" height="88" viewBox="0 0 88 88" fill="none" data-id="element-1137">
          <circle cx="44" cy="44" r="40" stroke={sign.colors.primary} strokeWidth="0.8" opacity="0.6" data-id="element-1138" />
          <circle cx="44" cy="44" r="30" stroke={sign.colors.primary} strokeWidth="0.6" strokeDasharray="3 4" opacity="0.7" data-id="element-1139" />
          {[0, 45, 90, 135].map(a => <line key={a} x1="44" y1="12" x2="44" y2="76" stroke={sign.colors.primary} strokeWidth="0.7" opacity="0.6" transform={`rotate(${a} 44 44)`} data-id="element-1140" />)}
          <path d="M44 26 L58 44 L44 62 L30 44 Z" stroke={sign.colors.primary} strokeWidth="1" fill={sign.colors.primary} fillOpacity="0.15" data-id="element-1141" />
          <circle cx="44" cy="44" r="4" fill={sign.colors.primary} data-id="element-1142" />
        </svg>
      </motion.div>

      {/* Editorial page-type label */}
      <p className="text-[10px] font-serif tracking-[0.5em] uppercase mb-3 opacity-80 text-center px-4" style={{
      color: sign.colors.accent
    }} data-id="element-1143">
        {label}
      </p>
      <p className="text-xl md:text-2xl font-serif italic px-6 text-center" style={{
      color: '#F5F0E1',
      textShadow: `0 0 20px ${sign.colors.primary}50`
    }} data-id="element-1144">
        Archivo en expansión
      </p>
      <p className="mt-4 text-xs font-serif tracking-[0.3em] uppercase opacity-60" style={{
      color: sign.colors.accent
    }} data-id="element-1145">
        {sign.name} · {String(index + 1).padStart(2, '0')}
      </p>
    </div>;
}